import { useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FilePen } from 'lucide-react';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Segmented } from '../../../components/Segmented';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { useCanView } from '../../../stores/session';
import { useInterviews, useMineCounts } from '../api/interviews-api';
import { InterviewRow } from '../components/InterviewRow';
import { InterviewsHeader } from '../components/InterviewsHeader';
import type { InterviewListContext } from '../interview-list-context';
import { useViewerZone } from '../hooks/use-interview-actions';
import styles from '../components/Interviews.module.css';

/**
 * SCH-03 — the interviews this person sits on, whatever their role (a hiring manager on
 * their own round included). Mobile-first: a panelist opens this on a phone between
 * meetings (CLAUDE.md §5).
 */
export function MyInterviewsPage(): ReactNode {
  const canView = useCanView('interview');
  const zone = useViewerZone();
  const navigate = useNavigate();
  const [when, setWhen] = useState<'upcoming' | 'past'>('upcoming');
  const filter = { view: 'mine', isPast: when === 'past' } as const;
  const query = useInterviews(filter);
  const counts = useMineCounts(canView).data;
  const listContext: InterviewListContext = { filter, label: when === 'past' ? 'past' : 'coming up' };

  if (!canView) return <ForbiddenState resource="interviews" contact="Your admin" />;

  return (
    <div className={styles.screen}>
      <InterviewsHeader current="mine" />
      <Card
        title="My interviews"
        meta="Interviews you are on the panel for"
        action={
          <Segmented
            label="Which interviews"
            value={when}
            options={[
              { value: 'upcoming', label: 'Coming up', ...(counts ? { count: counts.upcoming } : {}) },
              { value: 'past', label: 'Past', ...(counts ? { count: counts.past } : {}) },
            ]}
            onChange={setWhen}
          />
        }
      >
        {query.isPending ? (
          <SkeletonLines count={5} />
        ) : query.error ? (
          <ServerErrorState onRetry={() => void query.refetch()} />
        ) : query.data.length === 0 ? (
          <EmptyState
            isInset
            title={when === 'upcoming' ? 'No interviews coming up' : 'No past interviews'}
            body={when === 'upcoming' ? 'When you are put on an interview, it appears here and you are notified.' : 'Interviews you have sat on appear here.'}
            action={
              <Button variant="outline" size="sm" onClick={() => void navigate({ to: '/interviews/availability' })}>
                Set my availability
              </Button>
            }
          />
        ) : (
          <ul className={styles.list}>
            {query.data.map((row) => (
              <InterviewRow
                key={row.id}
                interview={row}
                zone={zone}
                hasDateTile
                listContext={listContext}
                action={
                  // INT-01 — the scorecard, from the list a panelist opens on their phone (D9).
                  row.status === 'cancelled' || row.status === 'candidate_no_show' ? undefined : (
                    <Button size="sm" variant="outline" icon={FilePen} onClick={() => void navigate({ to: '/interviews/$interviewId/scorecard', params: { interviewId: row.id } })}>
                      Scorecard
                    </Button>
                  )
                }
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
